import React, { useState, useCallback, useMemo, useContext } from 'react';
import { css } from '@emotion/react';
import {
    Grid,
    TextField,
    InputAdornment,
    Checkbox,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
} from '@material-ui/core';
import { Search as SearchIcon, LocalShippingOutlined as LocalShippingOutlinedIcon } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { ROUTE_COLORS } from '../constants';
import generateCSV from '@/utilities/createCSV';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { ROUTE_COLUMNS } from '@/constants/csvRouteColumns';
import { ROUTE_COLUMNS_EXT } from '@/constants/csvRouteColumnsExt';
import { ROUTE_SALESFORCE_COLUMNS } from '@/constants/csvSalesforceRouteColumns';
import { ROUTE_GE_DELIVERS_COLUMNS, buildGEDeliversFilename } from '@/constants/csvGEDeliversRouteColumns';
import { useClientUser } from '@/hooks';
import { SALESFORCE_ROUTE_EXPORT, GE_DELIVERS_ROUTE_EXPORT } from '@/constants/featureFlags';
import { QUERY_BY_LEADER } from '@/graphql/queries/teammates';
import { useQuery } from '@apollo/client';
import { PlanningContext } from '../context';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { MODALS } from '../constants';
import { Body2, GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import ManifestPDF from './ManifestPDF';
import { manifestCsvHeader, manifestCsvItemRow, manifestCsvOrderRow } from '../util/manifestCsv';
import { nextUniqueStopTime } from '../util/uniqueStopTimes';
import { getRouteShipperOptions, orderMatchesShippers } from '../util/routeShippers';
import { routeDisplayName } from '@/utilities/routeNames';
import { prepareManifestData } from '@/utilities/prepareManifestData';
import { pdf } from '@react-pdf/renderer';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { genAttributes } from '@onward-delivery/core';
import { capitalize } from 'lodash';

const RouteExportModal = () => {
    const {
        modalOpen,
        setModalOpen,
        deliveryDate,
        updateDeliveryDate,
        state: { routes, loading },
    } = useContext(PlanningContext);
    const { user_id, userType, circles, partnerCarriersById } = useClientUser();

    const { data: { results: allDrivers } = {} } = useQuery(QUERY_BY_LEADER, {
        variables: { leader_id: user_id },
        skip: !user_id,
    });

    const [selectedRoutes, setSelectedRoutes] = useState([]);
    const [searchString, setSearchString] = useState('');
    const [itemExtension, setItemExtension] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');
    const [selectedShippers, setSelectedShippers] = useState([]);

    const visibleRoutes = useMemo(() => {
        return searchString ? routes.filter((r) => r.route_number.toString().includes(searchString)) : routes;
    }, [routes, searchString]);

    const selectedRoutesList = useMemo(
        () => visibleRoutes.filter((route) => selectedRoutes.includes(route.route_id)),
        [visibleRoutes, selectedRoutes]
    );

    // Shipper multi-select (Salesforce / GE Delivers only)
    const isShipperFormat = ['salesforce', 'ge_delivers'].includes(exportFormat);
    const shipperOptions = useMemo(() => getRouteShipperOptions(selectedRoutesList), [selectedRoutesList]);
    const selectedShipperOptions = useMemo(
        () => shipperOptions.filter((o) => selectedShippers.includes(o.value)),
        [shipperOptions, selectedShippers]
    );
    const selectedShipperValues = selectedShipperOptions.map((o) => o.value);

    const handleModalClose = () => {
        setModalOpen(null);
        setSelectedRoutes([]);
        setSelectedShippers([]);
    };

    const toggleRoutes = useCallback(() => {
        if (selectedRoutes.length !== visibleRoutes.length) {
            setSelectedRoutes(visibleRoutes.map((route) => route.route_id));
        } else {
            setSelectedRoutes([]);
        }
    }, [visibleRoutes, selectedRoutes]);

    const toggleRoute = (route, isSelected) =>
        setSelectedRoutes((prevState) =>
            isSelected ? prevState.filter((r) => r !== route.route_id) : [...prevState, route.route_id]
        );

    const updateVisibleRoutes = (e) => {
        const searchString = e.target.value;

        setSearchString(searchString);
    };

    const generateManifestCSV = () => {
        const escape = (v) =>
            `"${String(v ?? '')
                .replace(/\n/g, ' ')
                .replace(/"/g, '""')}"`;
        const toLine = (cells) => cells.map(escape).join(',');

        const rows = [manifestCsvHeader(itemExtension).join(',')];

        for (const route of selectedRoutesList) {
            const pdfData = prepareManifestData(route, allDrivers || [], { partnerCarriersById });
            for (const order of pdfData.orders) {
                if (itemExtension) {
                    for (const item of order.items) {
                        rows.push(toLine(manifestCsvItemRow(pdfData, order, item)));
                    }
                } else {
                    rows.push(toLine(manifestCsvOrderRow(pdfData, order)));
                }
            }
        }

        const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const filename = `${deliveryDate} Routes Manifest.csv`;
        const autoclick = document.createElement('a');
        const payload = URL.createObjectURL(blob);
        autoclick.setAttribute('href', payload);
        autoclick.setAttribute('download', filename);
        autoclick.style.visibility = 'hidden';
        document.body.appendChild(autoclick);
        autoclick.click();
        document.body.removeChild(autoclick);
        setTimeout(() => URL.revokeObjectURL(payload), 100);

        handleModalClose();
    };

    const generateRoutePdfs = async () => {
        for (const route of selectedRoutesList) {
            const pdfData = prepareManifestData(route, allDrivers || [], { partnerCarriersById });
            const pdfComponent = <ManifestPDF pdfData={pdfData} includeItemDetails={itemExtension} />;
            const blob = await pdf(pdfComponent).toBlob();

            const filename = `${route.route_number}_${deliveryDate}.pdf`;
            const autoclick = document.createElement('a');
            const payload = URL.createObjectURL(blob);
            autoclick.setAttribute('href', payload);
            autoclick.setAttribute('download', filename);
            autoclick.style.visibility = 'hidden';
            document.body.appendChild(autoclick);
            autoclick.click();
            document.body.removeChild(autoclick);

            // Clean up object URL
            setTimeout(() => URL.revokeObjectURL(payload), 100);
        }

        handleModalClose();
    };

    const generateRoutesCSV = (routeColumns, opts = {}) => {
        const data = selectedRoutesList.reduce((acc, route) => {
            const driver = allDrivers.find((d) => d.teammate_id === route.driver_id) || {};

            const order = route?.orders?.[0]?.order || {};
            const { zip } = genAttributes(order);
            const tz = zipcode_to_timezone.lookup(order[zip]) || Intl.DateTimeFormat().resolvedOptions().timeZone;

            let lastScheduledStart = null;

            // Key includes duplicate_override so DISCARD and DUPLICATE mappings for the
            // same order coexist in the map. getMapping prefers the active (DISCARD)
            // mapping and falls back to DUPLICATE only when no active one exists.
            const mappingByOrderId = new Map(
                (route.orders || []).map((m) => [`${m.order_id}_${m.duplicate_override ?? 'DISCARD'}`, m])
            );
            const getMapping = (order_id) =>
                mappingByOrderId.get(`${order_id}_DISCARD`) || mappingByOrderId.get(`${order_id}_DUPLICATE`);

            const [stopData] = FTLStopHelpers.getStopSequence(route.stopsByRouteId).reduce(
                ([data, idx], stop) => {
                    // Skip pickup stops
                    if (stop.type === 'PICKUP' && !stop.returns?.length) return [data, idx];

                    // Skip final return dropoff stops (returns delivered back to depot)
                    if (opts?.skipFinalDropoff && FTLStopHelpers.isFinalDropoff(stop)) return [data, idx];

                    let stopOrderIds = (stop.orders || []).filter((order_id) => !!getMapping(order_id));

                    if (opts?.uniqueTimes) {
                        stopOrderIds = [...stopOrderIds].sort((a, b) => {
                            const orderA = getMapping(a)?.order;
                            const orderB = getMapping(b)?.order;
                            const aNum =
                                (orderA?.references || []).find((ref) => ref.key === 'appointment_number')?.value || '';
                            const bNum =
                                (orderB?.references || []).find((ref) => ref.key === 'appointment_number')?.value || '';
                            return String(aNum).localeCompare(String(bNum), undefined, { numeric: true });
                        });
                    }

                    const stopOrders = stopOrderIds.map((order_id, orderIdx) => {
                        const _order = getMapping(order_id)?.order;

                        // Grab dropoff comments
                        const comments = (_order?.notes || [])
                            .filter((n) => {
                                if (n.private_to && n.private_to !== capitalize(userType)) return false;
                                return n.type === 'Delivery';
                            })
                            .map((n) => n.note);

                        // Unique start and end times for salesforce export
                        let scheduledStart = stop.del_window_start ? new Date(stop.del_window_start) : null;
                        let scheduledEnd = stop.del_window_end ? new Date(stop.del_window_end) : null;

                        if (opts?.uniqueTimes) {
                            ({ scheduledStart, scheduledEnd, lastScheduledStart } = nextUniqueStopTime(
                                scheduledStart,
                                scheduledEnd,
                                lastScheduledStart
                            ));
                        }

                        return {
                            route,
                            stop,
                            orders: [{ ..._order, comments }],
                            driver,
                            opt: { tz, stopSequence: idx + orderIdx, scheduledStart, scheduledEnd },
                        };
                    });

                    // Apply exclusion filters after sequencing so dropped rows
                    // don't shift stopSequence for the orders that remain.
                    const filteredStopOrders = stopOrders
                        .map((row) => ({
                            ...row,
                            orders: row.orders.filter((order) => {
                                const m = getMapping(order.order_id);
                                if (opts?.skipInactive && m?.duplicate_override === 'DUPLICATE') return false;
                                if (opts?.shipperIds && !orderMatchesShippers(m?.order, opts.shipperIds)) {
                                    return false;
                                }
                                return true;
                            }),
                        }))
                        .filter((row) => row.orders.length > 0);

                    data.push(...filteredStopOrders);
                    return [data, idx + stopOrders.length];
                },
                [[], 1]
            );

            return [...acc, ...stopData];
        }, []);

        const blob = generateCSV(routeColumns, data, { noHeaders: opts.noHeaders });
        const filename =
            typeof opts.filename === 'function' ? opts.filename(data) : opts.filename || `${deliveryDate} Routes.csv`;
        const autoclick = document.createElement('a');
        const payload = URL.createObjectURL(blob);
        autoclick.setAttribute('href', payload);
        autoclick.setAttribute('download', filename);
        autoclick.style.visibility = 'hidden';
        document.body.appendChild(autoclick);
        autoclick.click();
        document.body.removeChild(autoclick);

        // Clean up object URL
        setTimeout(() => URL.revokeObjectURL(payload), 100);

        handleModalClose();
    };

    const handleExport = async () => {
        if (exportFormat === 'pdf') {
            await generateRoutePdfs();
        } else if (exportFormat === 'manifest_csv') {
            generateManifestCSV();
        } else if (circles?.[SALESFORCE_ROUTE_EXPORT] && exportFormat === 'salesforce') {
            generateRoutesCSV(ROUTE_SALESFORCE_COLUMNS, {
                skipFinalDropoff: true,
                uniqueTimes: true,
                shipperIds: selectedShipperValues,
                skipInactive: true,
            });
        } else if (circles?.[GE_DELIVERS_ROUTE_EXPORT] && exportFormat === 'ge_delivers') {
            generateRoutesCSV(ROUTE_GE_DELIVERS_COLUMNS, {
                skipFinalDropoff: true,
                uniqueTimes: false,
                shipperIds: selectedShipperValues,
                skipInactive: true,
                noHeaders: true,
                filename: (data) => buildGEDeliversFilename(data, new Date()),
            });
        } else {
            const routeColumns = itemExtension ? ROUTE_COLUMNS_EXT : ROUTE_COLUMNS;
            generateRoutesCSV(routeColumns);
        }
    };

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_EXPORT} onClose={handleModalClose}>
            <ModalTitle>Download Route Summaries</ModalTitle>
            <ModalContent>
                <GridItemRow
                    css={css`
                        input {
                            font-size: 14px;
                            font-weight: 400;
                            padding: 8px 12px 12px 12px;
                        }
                    `}
                >
                    <Grid
                        css={css`
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            margin-right: 0.5rem;
                        `}
                    >
                        <Body2>Select All</Body2>
                        <Checkbox
                            onChange={toggleRoutes}
                            color="primary"
                            checked={selectedRoutes.length === visibleRoutes.length}
                        />
                    </Grid>
                    <TextField
                        type="date"
                        variant="outlined"
                        disabled={loading}
                        value={deliveryDate}
                        onChange={(e) => updateDeliveryDate(e.target.value)}
                        css={css`
                            margin-left: 0.5rem;
                            margin-right: 0.5rem;
                        `}
                    />
                    <TextField
                        size="small"
                        variant="outlined"
                        color="primary"
                        value={searchString}
                        onChange={updateVisibleRoutes}
                        placeholder="Search..."
                        InputProps={{
                            style: { width: '160px' },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        css={css`
                            margin-left: 0.5rem;
                        `}
                    />
                </GridItemRow>
                {visibleRoutes.map((route, idx) => {
                    const isSelected = !!selectedRoutes.find((r) => r === route.route_id);

                    return (
                        <GridItemRow
                            key={route.route_id}
                            css={css`
                                ${isSelected &&
                                `
                                    background: rgba(141, 141, 141, 0.2);
                                    border-top: 1px solid #d1d1d1;
                                    border-bottom: 1px solid #d1d1d1;
                                `}
                            `}
                        >
                            <Grid item>
                                <Checkbox
                                    onChange={() => toggleRoute(route, isSelected)}
                                    color="primary"
                                    checked={isSelected}
                                />
                                <Body2>{routeDisplayName(route)}</Body2>
                            </Grid>
                            <LocalShippingOutlinedIcon
                                css={css`
                                    color: ${ROUTE_COLORS[idx]};
                                    margin-right: 0.5rem;
                                `}
                            />
                        </GridItemRow>
                    );
                })}
            </ModalContent>
            <GridItemRow
                css={css`
                    margin-top: 1rem;
                    margin-bottom: 0.5rem;
                    padding-left: 16px;
                `}
            >
                <Grid item>
                    <Body2
                        css={css`
                            margin-bottom: 0.5rem;
                        `}
                    >
                        Export Format
                    </Body2>
                </Grid>
                <Grid item>
                    <FormControl component="fieldset">
                        <RadioGroup value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} row>
                            <FormControlLabel value="pdf" control={<Radio color="primary" />} label="PDF" />
                            <FormControlLabel value="manifest_csv" control={<Radio color="primary" />} label="CSV" />
                            <FormControlLabel value="csv" control={<Radio color="primary" />} label="Legacy CSV" />
                            {circles?.[SALESFORCE_ROUTE_EXPORT] && (
                                <FormControlLabel
                                    value="salesforce"
                                    control={<Radio color="primary" />}
                                    label="Salesforce"
                                />
                            )}
                            {circles?.[GE_DELIVERS_ROUTE_EXPORT] && (
                                <FormControlLabel
                                    value="ge_delivers"
                                    control={<Radio color="primary" />}
                                    label="GE Delivers"
                                />
                            )}
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </GridItemRow>
            {isShipperFormat && (
                <GridItemRow
                    css={css`
                        justify-content: center;
                        margin-top: 0.5rem;
                    `}
                >
                    <Grid
                        item
                        css={css`
                            width: 360px;
                            max-width: 100%;
                        `}
                    >
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            value={selectedShipperOptions}
                            onChange={(e, options) => setSelectedShippers(options.map((o) => o.value))}
                            options={shipperOptions}
                            getOptionSelected={(option, value) => option.value === value.value}
                            getOptionLabel={(option) => option.label}
                            renderOption={(option, { selected }) => (
                                <li key={option.value}>
                                    <Checkbox color="primary" checked={selected} />
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" label="Shippers to include" />
                            )}
                        />
                    </Grid>
                </GridItemRow>
            )}
            {!['salesforce', 'ge_delivers'].includes(exportFormat) && (
                <GridItemRow
                    css={css`
                        input {
                            font-size: 14px;
                            font-weight: 400;
                            padding: 8px 12px 12px 12px;
                            margin-top: 0.5rem !important;
                        }
                    `}
                >
                    <Grid item>
                        <Checkbox
                            onChange={() => setItemExtension((prev) => !prev)}
                            color="primary"
                            checked={itemExtension}
                        />
                        <Body2>Include Stop Level Item Information</Body2>
                    </Grid>
                </GridItemRow>
            )}
            <ModalActions>
                <SecondaryButton onClick={handleModalClose}>Cancel</SecondaryButton>
                <PrimaryButton
                    onClick={handleExport}
                    disabled={loading || !selectedRoutes.length || (isShipperFormat && !selectedShipperValues.length)}
                >
                    Download Routes ({selectedRoutes.length})
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default RouteExportModal;
